/**
 * Questionnaires Page - Unified view with Templates and Supplier Responses tabs
 *
 * This page combines:
 * - Tab 1: Templates - Admin configuration for questionnaire templates
 * - Tab 2: Supplier Responses - Operational tracking of supplier questionnaire completion
 *
 * Addresses client requirement to have both template management AND operational
 * supplier response tracking accessible from the Questionnaires menu.
 */
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ClipboardList, FileText, Users } from 'lucide-react';

// Import the existing components as tab content
import QualificationTemplates from './QualificationTemplates';
import SupplierQualifications from './SupplierQualifications';

export default function Questionnaires() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'responses';

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="px-6 pt-6">
        <div className="flex items-center gap-3 mb-2">
          <ClipboardList className="h-8 w-8 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Questionnaires</h1>
        </div>
        <p className="text-muted-foreground">
          Manage questionnaire templates and track supplier completion status
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="px-6 border-b">
          <TabsList className="h-12 bg-transparent p-0 gap-4">
            <TabsTrigger
              value="responses"
              className="relative h-12 px-4 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent hover:bg-muted/50"
            >
              <Users className="h-4 w-4 mr-2" />
              Supplier Responses
            </TabsTrigger>
            <TabsTrigger
              value="templates"
              className="relative h-12 px-4 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent hover:bg-muted/50"
            >
              <FileText className="h-4 w-4 mr-2" />
              Templates
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Tab Content */}
        <TabsContent value="responses" className="mt-0">
          <SupplierQualifications />
        </TabsContent>

        <TabsContent value="templates" className="mt-0">
          <QualificationTemplates />
        </TabsContent>
      </Tabs>
    </div>
  );
}
